import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import ServiceAuditDetailDialog, {
  formatServiceAuditTimestamp,
} from "../components/service/ServiceAuditDetailDialog";
import { Button } from "../components/ui/button";
import { DataValue, PassiveBadge, displayValue } from "../components/ui/data-presentation";
import {
  LIST_PAGE_SIZE_OPTIONS,
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import {
  DateRangeSelector,
  TopLevelFilterArea,
  TopLevelFilterClear,
  TopLevelFilterField,
  TopLevelFilterRow,
  TopLevelFilterSearch,
  TopLevelSelectFilter,
  TopLevelTextFilter,
  hasTopLevelFilterConstraints,
  useSearchFilterState,
} from "../components/ui/search-filter";
import WarehouseTabs from "../components/WarehouseTabs";
import { formatApiError } from "../lib/api";
import { listServiceAuditLogs } from "../lib/serviceApi";
import {
  SERVICE_AUDIT_ACTION_LABELS,
  SERVICE_AUDIT_ACTION_OPTIONS,
  SERVICE_AUDIT_BADGE_CLASSES,
  SERVICE_AUDIT_BADGE_TONES,
  SERVICE_AUDIT_FALLBACK_BADGE_TONE,
  SERVICE_AUDIT_ENTITY_LABELS,
  SERVICE_AUDIT_ENTITY_OPTIONS,
} from "../lib/serviceConstants";

const DEFAULT_PAGE_SIZE = 10;
const FILTER_KEYS = ["action", "entity_type", "ticket_number", "actor"];
const EMPTY_FILTERS = Object.freeze(Object.fromEntries(FILTER_KEYS.map((key) => [key, ""])));
const SERVICE_AUDIT_WAREHOUSES = [
  { id: "jakarta", code: "jakarta", name: "Jakarta" },
  { id: "surabaya", code: "surabaya", name: "Surabaya" },
  { id: "bali", code: "bali", name: "Bali" },
];
const SERVICE_AUDIT_BRANCHES = SERVICE_AUDIT_WAREHOUSES.map((warehouse) => warehouse.id);
const EMPTY_TAB_COUNTS = Object.freeze({
  __all__: 0,
  jakarta: 0,
  surabaya: 0,
  bali: 0,
});
const VALID_BRANCHES = new Set(SERVICE_AUDIT_WAREHOUSES.map((warehouse) => warehouse.id));

export function normalizeServiceAuditPage(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

export function normalizeServiceAuditPageSize(value) {
  const parsed = Number.parseInt(value, 10);
  return LIST_PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

export function buildServiceAuditSearchParams(filters = {}, page = 1, branch = "", pageSize = DEFAULT_PAGE_SIZE, baseParams) {
  // Preserve the existing helper signature while keeping C-21 filters out of the URL.
  void filters;
  const params = new URLSearchParams(baseParams);
  if (VALID_BRANCHES.has(branch)) params.set("branch", branch);
  else params.delete("branch");
  params.set("page", String(normalizeServiceAuditPage(page)));
  const normalizedPageSize = normalizeServiceAuditPageSize(pageSize);
  if (normalizedPageSize !== DEFAULT_PAGE_SIZE) params.set("page_size", String(normalizedPageSize));
  return params;
}

function safeText(value) {
  return String(displayValue(value));
}

export default function ServiceAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawBranch = (searchParams.get("branch") || "").trim().toLowerCase();
  const branch = VALID_BRANCHES.has(rawBranch) ? rawBranch : "";
  const branchIsNormalized = !searchParams.has("branch") || searchParams.get("branch") === branch;
  const page = normalizeServiceAuditPage(searchParams.get("page"));
  const pageSize = normalizeServiceAuditPageSize(searchParams.get("page_size"));
  const pageIsNormalized = searchParams.get("page") === String(page);
  const pageSizeIsNormalized = !searchParams.has("page_size") || searchParams.get("page_size") === String(pageSize);
  const filters = useSearchFilterState({
    initialState: {
      topLevelDraft: { ...EMPTY_FILTERS },
      topLevelApplied: { ...EMPTY_FILTERS },
    },
    dateRangeMode: "draft",
    onPageReset: () => setSearchParams(buildServiceAuditSearchParams(EMPTY_FILTERS, 1, branch, pageSize, searchParams), { replace: true }),
  });
  const draftFilters = filters.state.topLevelDraft;
  const appliedFilters = filters.state.topLevelApplied;
  const dateRangeDraft = filters.state.dateRangeDraft;
  const dateRangeApplied = filters.state.dateRangeApplied;
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, total_pages: 0 });
  const [tabCounts, setTabCounts] = useState({ ...EMPTY_TAB_COUNTS });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const filtersPresent = hasTopLevelFilterConstraints(filters.state);
  const filtersActive = FILTER_KEYS.some((key) => Boolean(appliedFilters[key])) || Boolean(dateRangeApplied.from || dateRangeApplied.to);

  useEffect(() => {
    const rawPage = searchParams.get("page");
    if (rawPage !== String(page) || !branchIsNormalized || !pageSizeIsNormalized) {
      setSearchParams(buildServiceAuditSearchParams(EMPTY_FILTERS, page, branch, pageSize, searchParams), { replace: true });
    }
  }, [branch, branchIsNormalized, page, pageSize, pageSizeIsNormalized, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!pageIsNormalized || !branchIsNormalized || !pageSizeIsNormalized) return undefined;
    async function load() {
      setLoading(true);
      setError("");
      setRecords([]);
      try {
        const response = await listServiceAuditLogs({
          ...appliedFilters,
          date_from: dateRangeApplied.from,
          date_to: dateRangeApplied.to,
          branch,
          page,
          page_size: pageSize,
        });
        if (cancelled) return;
        const responseTotalPages = response?.pagination?.total_pages ?? 0;
        if (responseTotalPages > 0 && page > responseTotalPages) {
          setSearchParams(buildServiceAuditSearchParams(appliedFilters, responseTotalPages, branch, pageSize, searchParams), { replace: true });
          return;
        }
        setRecords(Array.isArray(response?.data) ? response.data : []);
        setPagination({
          page: responseTotalPages > 0
            ? Math.min(response?.pagination?.page ?? page, responseTotalPages)
            : page,
          page_size: response?.pagination?.page_size ?? pageSize,
          total: response?.pagination?.total ?? 0,
          total_pages: response?.pagination?.total_pages ?? 0,
        });
        if (branch) {
          setTabCounts((current) => ({
            ...current,
            [branch]: response?.pagination?.total ?? 0,
          }));
        }
      } catch (requestError) {
        if (cancelled) return;
        setPagination({ page, page_size: pageSize, total: 0, total_pages: 0 });
        setError(formatApiError(requestError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters, branch, branchIsNormalized, dateRangeApplied, page, pageIsNormalized, pageSize, pageSizeIsNormalized, retryKey, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!pageIsNormalized || !branchIsNormalized) return undefined;
    async function loadTabCounts() {
      const results = await Promise.allSettled(
        SERVICE_AUDIT_BRANCHES.map((countBranch) =>
          listServiceAuditLogs({
            ...appliedFilters,
            date_from: dateRangeApplied.from,
            date_to: dateRangeApplied.to,
            branch: countBranch,
            page: 1,
            page_size: 1,
          })
        )
      );
      if (cancelled) return;
      const counts = { ...EMPTY_TAB_COUNTS };
      SERVICE_AUDIT_BRANCHES.forEach((countBranch, index) => {
        const result = results[index];
        if (result.status === "fulfilled") {
          counts[countBranch] = result.value?.pagination?.total ?? 0;
        }
      });
      counts.__all__ = SERVICE_AUDIT_BRANCHES.reduce(
        (total, countBranch) => total + counts[countBranch],
        0
      );
      setTabCounts(counts);
    }
    loadTabCounts();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters, branchIsNormalized, dateRangeApplied, pageIsNormalized, retryKey]);

  const updateDraft = (key, value) => {
    filters.setTopLevelDraft((current) => ({ ...current, [key]: value }));
  };
  const changePage = (nextPage) => {
    setSearchParams(buildServiceAuditSearchParams(appliedFilters, nextPage, branch, pageSize, searchParams), { replace: true });
  };
  const changeBranch = (nextBranch) => {
    setSearchParams(buildServiceAuditSearchParams(appliedFilters, 1, nextBranch, pageSize, searchParams), { replace: true });
  };
  const totalPages = pagination.total_pages;
  const emptyMessage = pagination.total > 0
    ? "No Service audit records are available on this page."
    : filtersActive
      ? "No Service audit records match the current filters."
      : "No Service audit history exists yet.";

  return (
    <OrdinaryPageBody data-testid="service-audit-page">
      <OrdinaryPageHeader title="Service Audit" actions={<RefreshButton onClick={() => setRetryKey((value) => value + 1)} loading={loading} accessibleLabel="Refresh service audit" data-testid="service-audit-refresh" />} data-testid="service-audit-page-header" />
      <ListTableShell>
        <WarehouseTabs
          warehouses={SERVICE_AUDIT_WAREHOUSES}
          active={branch || "__all__"}
          onChange={(warehouseId) => changeBranch(warehouseId === "__all__" ? "" : warehouseId)}
          showAll
          counts={tabCounts}
          testidPrefix="service-audit-branch"
        />
        <TopLevelFilterArea className="rounded-none border-x-0 border-t-0 bg-zinc-50/50 px-5 py-3" data-testid="service-audit-filters">
          <TopLevelFilterRow className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(10rem,1.15fr)_minmax(11rem,1.2fr)_minmax(10rem,1fr)_auto]">
            <TopLevelFilterField label="Action" htmlFor="service-audit-action-filter">
              <TopLevelSelectFilter id="service-audit-action-filter" value={draftFilters.action || ""} onChange={(event) => updateDraft("action", event.target.value)} data-testid="service-audit-action-filter">
                <option value="">All actions</option>
                {SERVICE_AUDIT_ACTION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </TopLevelSelectFilter>
            </TopLevelFilterField>
            <TopLevelFilterField label="Entity Type" htmlFor="service-audit-entity-filter">
              <TopLevelSelectFilter id="service-audit-entity-filter" value={draftFilters.entity_type || ""} onChange={(event) => updateDraft("entity_type", event.target.value)} data-testid="service-audit-entity-filter">
                <option value="">All entity types</option>
                {SERVICE_AUDIT_ENTITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </TopLevelSelectFilter>
            </TopLevelFilterField>
            <TopLevelFilterField label="Ticket Number" htmlFor="service-audit-ticket-filter">
              <TopLevelTextFilter id="service-audit-ticket-filter" value={draftFilters.ticket_number || ""} onChange={(event) => updateDraft("ticket_number", event.target.value)} placeholder="Ticket number" data-testid="service-audit-ticket-filter" />
            </TopLevelFilterField>
            <TopLevelFilterField label="Actor" htmlFor="service-audit-actor-filter">
              <TopLevelTextFilter id="service-audit-actor-filter" value={draftFilters.actor || ""} onChange={(event) => updateDraft("actor", event.target.value)} placeholder="Actor email" data-testid="service-audit-actor-filter" />
            </TopLevelFilterField>
            <DateRangeSelector mode="draft" value={dateRangeDraft} onChange={filters.setDateRangeDraft} id="service-audit-filter-date" data-testid="service-audit-filter-date" />
            <div className="flex items-center justify-end gap-1 self-end">
              <TopLevelFilterClear onClick={filters.clearTopLevelFilters} disabled={!filtersPresent} data-action-category="utility" data-testid="service-audit-filter-clear">Clear All</TopLevelFilterClear>
              <TopLevelFilterSearch onClick={filters.applyTopLevelFilters} data-testid="service-audit-filter-search" />
            </div>
          </TopLevelFilterRow>
        </TopLevelFilterArea>

        <ListTableViewport>
          <ListTable className="min-w-[1420px]" data-testid="service-audit-table">
            <thead><tr><th>Timestamp</th><th>Actor</th><th>Action</th><th>Ticket Number</th><th>Entity Type</th><th>Entity Reference</th><th>Summary</th><th>Details</th></tr></thead>
            <tbody>
              {loading ? (
                <ListTableState colSpan={8} kind="loading" testId="service-audit-loading">Loading Service audit history...</ListTableState>
              ) : error ? (
                <ListTableState colSpan={8} kind="error" testId="service-audit-error" onRetry={() => setRetryKey((value) => value + 1)}>{error}</ListTableState>
              ) : records.length === 0 ? (
                <ListTableState colSpan={8} kind={filtersActive ? "filtered-empty" : "empty"} testId="service-audit-empty">
                  <div>{emptyMessage}</div>
                </ListTableState>
              ) : records.map((record) => {
                const actionLabel = record.action_label || SERVICE_AUDIT_ACTION_LABELS[record.action] || "Unknown Service Action";
                const badgeTone = SERVICE_AUDIT_BADGE_TONES[record.action] || SERVICE_AUDIT_FALLBACK_BADGE_TONE;
                const badgeClass = badgeTone === "custom" ? SERVICE_AUDIT_BADGE_CLASSES[record.action] : undefined;
                return (
                  <tr key={record.id} data-testid={`service-audit-row-${record.id}`}>
                    <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatServiceAuditTimestamp(record.timestamp)}</td>
                    <td className="max-w-[220px] text-xs"><DataValue value={record.actor} containment="truncate" /></td>
                    <td><PassiveBadge tone={badgeTone} className={badgeClass} data-testid={`service-audit-badge-${record.id}`}>{actionLabel}</PassiveBadge></td>
                    <td className="max-w-[200px] text-xs font-semibold"><DataValue value={record.ticket_number} containment="truncate" /></td>
                    <td className="text-xs"><DataValue value={SERVICE_AUDIT_ENTITY_LABELS[record.entity_type] || safeText(record.entity_type)} /></td>
                    <td className="max-w-[220px] text-xs"><DataValue value={record.entity_reference} containment="truncate" /></td>
                    <td className="max-w-[300px] text-xs"><DataValue value={record.summary || actionLabel} /></td>
                    <td><Button type="button" variant="info" size="row-icon" title="View details" onClick={() => setSelectedRecord(record)} data-action-category="view-info" aria-label={`View details for ${actionLabel}`}> <Eye className="h-3.5 w-3.5" /></Button></td>
                  </tr>
                );
              })}
            </tbody>
          </ListTable>
        </ListTableViewport>

        <ListPagination
          page={pagination.page}
          pageSize={pageSize}
          total={pagination.total}
          totalPages={totalPages}
          loading={loading}
          onPrevious={() => changePage(Math.max(1, page - 1))}
          onNext={() => changePage(page + 1)}
          onPageSizeChange={(nextPageSize) => setSearchParams(buildServiceAuditSearchParams(appliedFilters, 1, branch, nextPageSize, searchParams), { replace: true })}
          pageSizeAriaLabel="Service audit rows per page"
          summaryTestId="service-audit-page-summary"
        />
      </ListTableShell>

      <ServiceAuditDetailDialog record={selectedRecord} open={Boolean(selectedRecord)} onOpenChange={(open) => { if (!open) setSelectedRecord(null); }} />
    </OrdinaryPageBody>
  );
}
